import { useState, useRef } from "react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", textPri:"#F0F6FF", textSec:"#94A3B8",
  textMute:"#4B5563", codeBg:"#060D18", codeStr:"#86EFAC", codeKw:"#7DD3FC",
  codeFn:"#FDE68A", codeCmt:"#475569", codeNum:"#F9A8D4",
};

/* ── Syntax highlighter ────────────────────────────────────── */
function highlight(code) {
  const kws = ["import","from","as","def","class","return","pass","if","else","elif",
    "for","while","in","not","and","or","True","False","None","with","raise","try",
    "except","finally","lambda","yield","super","self","async","await","nonlocal","global","break","continue"];
  const builtins = ["print","range","len","sleep","append","format","enumerate",
    "isinstance","hasattr","getattr","setattr","list","dict","set","tuple","str","int","float","next","min","max","abs","sum"];
  const threadKws = ["Thread","ThreadPoolExecutor","ProcessPoolExecutor","Future","as_completed",
    "submit","map","wait","FIRST_COMPLETED","FIRST_EXCEPTION","ALL_COMPLETED","cancel","cancelled",
    "done","result","exception","Lock","RLock","Semaphore","Event","Condition","Queue","asyncio",
    "gather","create_task","run","run_in_executor","get_event_loop","get_running_loop","partial",
    "current_thread","threading","time","random","concurrent","futures","shutdown","add_done_callback",
    "cpu_count","os","TimeoutError","ZeroDivisionError","ConnectionError","task_done","put","get"];
  return code.split("\n").map((line, li) => {
    if (line.trimStart().startsWith("#")) {
      return <div key={li} style={{minHeight:"1.4em",color:T.codeCmt}}>{line}</div>;
    }
    const tokens = [];
    let i = 0;
    while (i < line.length) {
      if (line[i]==="#") {
        tokens.push(<span key={i} style={{color:T.codeCmt}}>{line.slice(i)}</span>);
        i=line.length; continue;
      }
      if (line[i]==="'" || line[i]==='"') {
        const q=line[i]; let j=i+1;
        while(j<line.length && line[j]!==q) j++;
        tokens.push(<span key={i} style={{color:T.codeStr}}>{line.slice(i,j+1)}</span>);
        i=j+1; continue;
      }
      if (/\d/.test(line[i]) && (i===0||!/\w/.test(line[i-1]))) {
        let j=i; while(j<line.length && /[\d_.x]/.test(line[j])) j++;
        tokens.push(<span key={i} style={{color:T.codeNum}}>{line.slice(i,j)}</span>);
        i=j; continue;
      }
      if (/[a-zA-Z_]/.test(line[i])) {
        let j=i; while(j<line.length && /\w/.test(line[j])) j++;
        const word=line.slice(i,j);
        let color=T.textPri;
        if (kws.includes(word)) color=T.codeKw;
        else if (threadKws.includes(word)) color=T.codeFn;
        else if (builtins.includes(word)) color=T.accent;
        tokens.push(<span key={i} style={{color}}>{word}</span>);
        i=j; continue;
      }
      tokens.push(<span key={i} style={{color:T.textSec}}>{line[i]}</span>);
      i++;
    }
    return <div key={li} style={{minHeight:"1.4em"}}>{tokens}</div>;
  });
}

/* ── CodeBlock ─────────────────────────────────────────────── */
function CodeBlock({ code, label="python" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{marginBottom:14,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        background:"#0D1929",padding:"5px 14px",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:11,color:T.accent,fontFamily:"monospace",letterSpacing:.5}}>{label}</span>
        <button onClick={()=>{navigator.clipboard.writeText(code);setCopied(true);setTimeout(()=>setCopied(false),1500);}}
          style={{fontSize:11,color:copied?T.green:T.textSec,background:"none",border:"none",cursor:"pointer",padding:"2px 8px",borderRadius:4}}>
          {copied?"✓ copied":"copy"}
        </button>
      </div>
      <div style={{background:T.codeBg,padding:"12px 16px",overflowX:"auto",
        fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12.5,lineHeight:1.65}}>
        {highlight(code)}
      </div>
    </div>
  );
}

/* ── Difficulty badge ──────────────────────────────────────── */
function Difficulty({ level }) {
  const cfg = {
    Easy:   { bg:"rgba(52,211,153,.12)",  color:T.green,  border:"rgba(52,211,153,.3)"  },
    Medium: { bg:"rgba(252,211,77,.12)",  color:T.yellow, border:"rgba(252,211,77,.3)"  },
    Hard:   { bg:"rgba(248,113,113,.12)", color:T.red,    border:"rgba(248,113,113,.3)" },
  }[level] || {};
  return (
    <span style={{fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",
      padding:"2px 8px",borderRadius:20,background:cfg.bg,color:cfg.color,
      border:`1px solid ${cfg.border}`,whiteSpace:"nowrap"}}>
      {level}
    </span>
  );
}

/* ── Concept tags ──────────────────────────────────────────── */
function ConceptTag({ label }) {
  return (
    <span style={{fontFamily:"monospace",fontSize:11,color:T.accent,
      background:"rgba(56,189,248,0.08)",border:`1px solid rgba(56,189,248,0.2)`,
      padding:"2px 7px",borderRadius:4,whiteSpace:"nowrap"}}>
      {label}
    </span>
  );
}

/* ── Exercise card ─────────────────────────────────────────── */
function ExCard({ num, title, difficulty, concepts=[], goal, hint, starter }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,
      marginBottom:16,overflow:"hidden"}}>
      <div style={{padding:"14px 18px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}>
          <span style={{fontSize:11,fontWeight:700,color:T.textMute,letterSpacing:1,
            textTransform:"uppercase",fontFamily:"monospace"}}>{num}</span>
          <span style={{fontSize:14,fontWeight:700,color:T.textPri,flex:1}}>{title}</span>
          <Difficulty level={difficulty}/>
        </div>
        {concepts.length>0 && (
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
            {concepts.map(c=><ConceptTag key={c} label={c}/>)}
          </div>
        )}
        <p style={{fontSize:13.5,color:T.textSec,lineHeight:1.7,margin:"0 0 10px"}}>{goal}</p>
        {starter && (
          <div>
            <div style={{fontSize:11,fontWeight:600,color:T.textMute,letterSpacing:1,
              textTransform:"uppercase",marginBottom:6}}>Starter Skeleton</div>
            <CodeBlock code={starter}/>
          </div>
        )}
        <button onClick={()=>setOpen(o=>!o)}
          style={{background:"none",border:`1px solid ${T.border}`,borderRadius:7,
            padding:"5px 12px",cursor:"pointer",fontSize:12,color:T.textMute,fontFamily:"inherit",marginTop:4}}>
          {open?"▾ Hide hint":"▸ Show hint"}
        </button>
        {open && (
          <div style={{marginTop:10,background:"#060D18",border:`1px solid ${T.green}33`,
            borderRadius:8,padding:"10px 14px",fontSize:12.5,color:T.green,
            fontFamily:"'JetBrains Mono',monospace",lineHeight:1.7}}>
            {hint}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Concept index table ───────────────────────────────────── */
function ConceptIndex() {
  const rows = [
    ["submit() / .result()",          "E1, E4, E5, M3, M8"],
    ["map()",                          "E2"],
    [".done()",                        "E3"],
    ["Exceptions in futures",          "E5, E6, M5, H6"],
    ["functools.partial",              "E7"],
    ["shutdown() / context manager",   "E8"],
    ["wait() (FIRST_COMPLETED / FIRST_EXCEPTION)", "M1, H6"],
    ["Timeouts",                       "M2"],
    ["Cancellation",                   "M3, H6"],
    ["as_completed()",                 "M4"],
    ["Retry patterns",                 "M5, H8"],
    ["Locks with pools",               "M6"],
    ["Sizing max_workers",             "M7, H1"],
    ["Deadlock pitfalls",              "M8"],
    ["ProcessPoolExecutor / GIL",      "H1"],
    ["Dynamic/streaming submission",   "H2"],
    ["add_done_callback()",            "H2, H4"],
    ["Building a pool from scratch",   "H3"],
    ["Chaining dependent tasks",       "H4"],
    ["Combining Semaphore + pool",     "H5"],
    ["asyncio + executor bridging",    "H7"],
    ["Custom executor wrappers",       "H8"],
  ];
  return (
    <div style={{overflowX:"auto",margin:"16px 0"}}>
      <table style={{borderCollapse:"collapse",width:"100%",background:T.card,fontSize:13}}>
        <thead>
          <tr style={{background:T.surface}}>
            {["Concept","Exercises"].map(h=>(
              <th key={h} style={{border:`1px solid ${T.border}`,padding:"8px 12px",
                textAlign:"left",color:T.textMute,fontSize:11,letterSpacing:".05em",fontWeight:600}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([concept,exs],i)=>(
            <tr key={i}>
              <td style={{border:`1px solid ${T.border}`,padding:"7px 12px",
                color:T.textSec,fontFamily:"monospace",fontSize:12}}>{concept}</td>
              <td style={{border:`1px solid ${T.border}`,padding:"7px 12px",
                color:T.accent,fontFamily:"monospace",fontSize:12}}>{exs}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Sidebar nav ───────────────────────────────────────────── */
const NAV = [
  { id:"overview", label:"Overview",   icon:"🧵" },
  { id:"easy",     label:"Easy (E1–E8)",   icon:"🟢" },
  { id:"medium",   label:"Medium (M1–M8)", icon:"🟡" },
  { id:"hard",     label:"Hard (H1–H8)",   icon:"🔴" },
  { id:"index",    label:"Concept Index",  icon:"📋" },
];

/* ═══════════════════════════════════════════════════════════ */
export default function PythonThreadingExercises3() {
  const [active, setActive] = useState("overview");
  const mainRef = useRef(null);
  const refs = useRef({});
  const SR = id => el => { refs.current[id] = el; };

  function go(id) {
    setActive(id);
    const el = refs.current[id], box = mainRef.current;
    if (el && box) box.scrollTop = el.offsetTop - 16;
  }

  const sectionHead = (label, color=T.orange) => (
    <div style={{fontFamily:"monospace",fontSize:11,letterSpacing:".12em",
      textTransform:"uppercase",color,marginBottom:10}}>{label}</div>
  );

  return (
    <div style={{display:"flex",height:"calc(100vh - 70px)",background:T.bg,fontFamily:"'Inter',sans-serif"}}>

      {/* Sidebar */}
      <div style={{width:196,flexShrink:0,background:T.surface,borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column"}}>
        <div style={{padding:"16px 14px 12px",borderBottom:`1px solid ${T.border}`}}>
          <div style={{fontSize:10,letterSpacing:1.2,textTransform:"uppercase",color:T.orange,fontWeight:700,marginBottom:4}}>
            Python · Threading
          </div>
          <div style={{fontSize:13,fontWeight:800,color:T.textPri,lineHeight:1.3}}>Exercise 3</div>
          <div style={{fontSize:11,color:T.textMute,marginTop:3}}>ThreadPool &amp; Futures</div>
        </div>
        <nav style={{padding:"8px 6px",flex:1,overflowY:"auto"}}>
          {NAV.map(item=>(
            <button key={item.id} onClick={()=>go(item.id)}
              style={{display:"flex",alignItems:"center",gap:8,width:"100%",textAlign:"left",
                padding:"8px 10px",borderRadius:7,border:"none",cursor:"pointer",marginBottom:2,
                background:active===item.id?"rgba(251,146,60,0.1)":"transparent",
                borderLeft:active===item.id?`2px solid ${T.orange}`:"2px solid transparent",
                color:active===item.id?T.orange:T.textSec,
                fontSize:13,fontWeight:active===item.id?700:400,transition:"all .15s"}}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={{padding:"10px 14px",borderTop:`1px solid ${T.border}`,fontSize:11,color:T.textMute,lineHeight:1.65}}>
          24 exercises<br/>Easy → Medium → Hard
        </div>
      </div>

      {/* Main */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{flexShrink:0,padding:"12px 32px",background:T.surface,borderBottom:`1px solid ${T.border}`,
          display:"flex",alignItems:"center",gap:16}}>
          <span style={{fontSize:11,letterSpacing:".8px",textTransform:"uppercase",color:T.orange,fontWeight:700}}>
            Python · Threading · Exercise 3
          </span>
          <span style={{fontSize:13,color:T.textMute}}>ThreadPoolExecutor &amp; Futures — 24 Practice Programs</span>
        </div>

        <div ref={mainRef} style={{flex:1,overflowY:"auto",padding:"32px 40px"}}>

          {/* ── Overview ── */}
          <div ref={SR("overview")} style={{paddingBottom:44,borderBottom:`1px solid ${T.border}`,marginBottom:36}}>
            {sectionHead("Overview")}
            <h2 style={{fontSize:"clamp(22px,3vw,30px)",fontWeight:800,color:T.textPri,margin:"0 0 14px"}}>
              ThreadPoolExecutor &amp; Futures
            </h2>
            <p style={{fontSize:14.5,color:T.textSec,lineHeight:1.7,maxWidth:"64ch",margin:"0 0 20px"}}>
              24 progressive exercises covering the full <code style={{color:T.accent,fontFamily:"monospace"}}>concurrent.futures</code> API —
              from your first <code style={{color:T.accent,fontFamily:"monospace"}}>submit()</code> call to building a custom retrying executor.
              Attempt each question before revealing the hint.
            </p>

            {/* Stats row */}
            <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:20}}>
              {[
                {label:"Easy",    count:"8 exercises", color:T.green,  bg:"rgba(52,211,153,.08)"},
                {label:"Medium",  count:"8 exercises", color:T.yellow, bg:"rgba(252,211,77,.08)"},
                {label:"Hard",    count:"8 exercises", color:T.red,    bg:"rgba(248,113,113,.08)"},
              ].map(({label,count,color,bg})=>(
                <div key={label} style={{background:bg,border:`1px solid ${color}33`,borderRadius:8,
                  padding:"10px 18px",flex:"1 1 120px"}}>
                  <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:1,color,marginBottom:2}}>{label}</div>
                  <div style={{fontSize:22,fontWeight:800,color:T.textPri}}>{count}</div>
                </div>
              ))}
            </div>

            <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
              padding:"12px 16px",fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
              <strong style={{color:T.textPri}}>Prerequisites:</strong>{" "}
              <code style={{color:T.accent,fontFamily:"monospace"}}>threading.Thread</code>,{" "}
              <code style={{color:T.accent,fontFamily:"monospace"}}>Lock</code>,{" "}
              <code style={{color:T.accent,fontFamily:"monospace"}}>Semaphore</code> — covered in Exercises 1 &amp; 2.
              {" "}For H7 you'll also need basic <code style={{color:T.accent,fontFamily:"monospace"}}>asyncio</code> familiarity.
            </div>
          </div>

          {/* ── EASY ── */}
          <div ref={SR("easy")} style={{paddingBottom:44,borderBottom:`1px solid ${T.border}`,marginBottom:36}}>
            {sectionHead("Easy · E1 – E8", T.green)}
            <h2 style={{fontSize:"clamp(18px,2.5vw,26px)",fontWeight:800,color:T.textPri,margin:"0 0 6px"}}>
              Easy Exercises
            </h2>
            <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
              padding:"12px 16px",marginBottom:20,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
              Core mechanics: <code style={{color:T.accent,fontFamily:"monospace"}}>submit()</code>,{" "}
              <code style={{color:T.accent,fontFamily:"monospace"}}>map()</code>,{" "}
              <code style={{color:T.accent,fontFamily:"monospace"}}>Future</code> methods,{" "}
              <code style={{color:T.accent,fontFamily:"monospace"}}>shutdown()</code>.
            </div>

            <ExCard num="E1" title="Your First Pool" difficulty="Easy"
              concepts={["ThreadPoolExecutor","submit()","Future.result()"]}
              goal="Write a function square(n) that returns n * n. Submit it for numbers 1–5 using a pool with max_workers=2, and print each result."
              hint="executor.submit(square, n) returns a Future immediately — call .result() on it to get the actual number back."
              starter={`from concurrent.futures import ThreadPoolExecutor

def square(n):
    return n * n

with ThreadPoolExecutor(max_workers=2) as executor:
    futures = [executor.submit(square, n) for n in range(1, 6)]
    for f in futures:
        print(f.result())  # blocks until that task finishes`}
            />

            <ExCard num="E2" title="map() for a Batch of Work" difficulty="Easy"
              concepts={["executor.map()"]}
              goal="Redo E1 using executor.map(square, [1,2,3,4,5]) instead of submit() in a loop. Print the results."
              hint="map() returns an iterator of *results*, not Future objects — you can loop over it directly. No .result() calls needed."
              starter={`from concurrent.futures import ThreadPoolExecutor

def square(n):
    return n * n

with ThreadPoolExecutor(max_workers=2) as executor:
    results = executor.map(square, [1, 2, 3, 4, 5])
    for r in results:
        print(r)  # results come in submission order, always`}
            />

            <ExCard num="E3" title="Checking .done() Before .result()" difficulty="Easy"
              concepts={["Future.done()","timing"]}
              goal="Submit a task that sleeps for 2 seconds and returns 'finished'. Immediately after submitting, print future.done(). Then sleep 3 seconds in your main code and print future.done() again."
              hint=".done() never blocks — it just answers True/False on the spot, unlike .result() which waits until the task completes."
              starter={`from concurrent.futures import ThreadPoolExecutor
import time

def slow_task():
    time.sleep(2)
    return 'finished'

with ThreadPoolExecutor(max_workers=1) as executor:
    future = executor.submit(slow_task)
    print('done immediately after submit?', future.done())  # False
    time.sleep(3)
    print('done after 3 seconds?', future.done())           # True
    print('result:', future.result())`}
            />

            <ExCard num="E4" title="Collecting Results in Submission Order" difficulty="Easy"
              concepts={["submit()","list of Futures","order preservation"]}
              goal="Submit 5 tasks with different random sleep times (0.1–1.0s) that each return their input number. Collect all Future objects into a list first, then loop through that list calling .result() on each. Confirm the printed order matches submission order, not completion order."
              hint="Because you stored the futures in a list before reading results, looping through the list and calling .result() on index 0 first forces your code to wait for whichever future is at index 0, even if a later one finished first."
              starter={`from concurrent.futures import ThreadPoolExecutor
import time, random

def task(n):
    time.sleep(random.uniform(0.1, 1.0))
    return n

with ThreadPoolExecutor(max_workers=5) as executor:
    futures = [executor.submit(task, n) for n in range(5)]
    # collect results AFTER submitting all tasks
    for f in futures:
        print(f.result())  # always prints 0, 1, 2, 3, 4 in order`}
            />

            <ExCard num="E5" title="Catching an Exception from a Task" difficulty="Easy"
              concepts={["exception propagation via .result()"]}
              goal="Write divide(a, b) that returns a / b. Submit divide(10, 0). Wrap your .result() call in try/except ZeroDivisionError and print a friendly message instead of letting the program crash."
              hint="The exception isn't raised at submit() time — it's stored inside the Future and only re-raised when you call .result()."
              starter={`from concurrent.futures import ThreadPoolExecutor

def divide(a, b):
    return a / b

with ThreadPoolExecutor(max_workers=1) as executor:
    future = executor.submit(divide, 10, 0)
    try:
        print(future.result())
    except ZeroDivisionError as e:
        print(f'caught: {e}')  # division by zero`}
            />

            <ExCard num="E6" title=".exception() Instead of try/except" difficulty="Easy"
              concepts={["Future.exception()"]}
              goal="Redo E5, but instead of try/except around .result(), call future.exception() first. If it returns None, call .result() to get the value; if it returns an exception object, print it without raising."
              hint=".exception() blocks until the task finishes (like .result() would), but returns the exception object instead of raising it — or None if the task succeeded."
              starter={`from concurrent.futures import ThreadPoolExecutor

def divide(a, b):
    return a / b

with ThreadPoolExecutor(max_workers=1) as executor:
    future = executor.submit(divide, 10, 0)
    exc = future.exception()   # blocks; returns the exception or None
    if exc is None:
        print('result:', future.result())
    else:
        print(f'task failed with: {exc}')`}
            />

            <ExCard num="E7" title="Using functools.partial with submit()" difficulty="Easy"
              concepts={["functools.partial","fixed arguments"]}
              goal="Write power(base, exponent). You want to submit power(2, x) for x in range(5) without writing a wrapper lambda. Use functools.partial(power, 2) and submit that. Also try submitting power directly with two arguments."
              hint="pool.submit(functools.partial(power, 2), x) — or just pool.submit(power, 2, x) directly. Both work; partial shines when you have many fixed arguments and want to name the partially-applied function."
              starter={`from concurrent.futures import ThreadPoolExecutor
import functools

def power(base, exponent):
    return base ** exponent

double_power = functools.partial(power, 2)  # base=2 is fixed

with ThreadPoolExecutor(max_workers=2) as executor:
    # approach 1: partial
    futures_a = [executor.submit(double_power, x) for x in range(5)]
    # approach 2: extra args to submit (same result)
    futures_b = [executor.submit(power, 2, x) for x in range(5)]
    print([f.result() for f in futures_a])
    print([f.result() for f in futures_b])`}
            />

            <ExCard num="E8" title="Explicit shutdown() vs. with Block" difficulty="Easy"
              concepts={["executor.shutdown(wait=True)","context managers"]}
              goal="Create a ThreadPoolExecutor WITHOUT a with block. Submit 3 slow tasks, then call executor.shutdown(wait=True) and print 'all done' only after it returns. Then rewrite it using with and confirm the behavior is identical."
              hint="Exiting a 'with ThreadPoolExecutor() as pool:' block calls shutdown(wait=True) automatically — that's why code after the with block only runs once every submitted task has finished."
              starter={`from concurrent.futures import ThreadPoolExecutor
import time

def slow(n):
    time.sleep(n)
    return n

# WITHOUT with-block
executor = ThreadPoolExecutor(max_workers=3)
for n in [1, 2, 1]:
    executor.submit(slow, n)
executor.shutdown(wait=True)   # blocks until all tasks are done
print('all done (explicit shutdown)')

# WITH with-block -- same behaviour, cleaner
with ThreadPoolExecutor(max_workers=3) as executor:
    for n in [1, 2, 1]:
        executor.submit(slow, n)
# shutdown(wait=True) is called here automatically
print('all done (with block)')`}
            />
          </div>

          {/* ── MEDIUM ── */}
          <div ref={SR("medium")} style={{paddingBottom:44,borderBottom:`1px solid ${T.border}`,marginBottom:36}}>
            {sectionHead("Medium · M1 – M8", T.yellow)}
            <h2 style={{fontSize:"clamp(18px,2.5vw,26px)",fontWeight:800,color:T.textPri,margin:"0 0 6px"}}>
              Medium Exercises
            </h2>
            <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
              padding:"12px 16px",marginBottom:20,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
              Coordination patterns: <code style={{color:T.accent,fontFamily:"monospace"}}>wait()</code>,{" "}
              timeouts, cancellation, <code style={{color:T.accent,fontFamily:"monospace"}}>as_completed()</code>,
              retry logic, locks, pool sizing, and deadlock pitfalls.
            </div>

            <ExCard num="M1" title="First One Wins: wait(FIRST_COMPLETED)" difficulty="Medium"
              concepts={["concurrent.futures.wait","FIRST_COMPLETED"]}
              goal="Submit 3 tasks with different sleep times (1s, 2s, 3s). Use wait(futures, return_when=FIRST_COMPLETED) to get back as soon as the fastest one finishes, without waiting for the other two. Print which task finished first."
              hint="wait() returns a named tuple (done, not_done) — check which future is in done to know who won. You can tag each future by storing it in a dict: {future: label}."
              starter={`from concurrent.futures import ThreadPoolExecutor, wait, FIRST_COMPLETED
import time

def slow_task(n, sleep_time):
    time.sleep(sleep_time)
    return f'task {n} finished'

with ThreadPoolExecutor(max_workers=3) as executor:
    futures = {
        executor.submit(slow_task, 1, 1): 'Task-1 (1s)',
        executor.submit(slow_task, 2, 2): 'Task-2 (2s)',
        executor.submit(slow_task, 3, 3): 'Task-3 (3s)',
    }
    done, not_done = wait(futures.keys(), return_when=FIRST_COMPLETED)
    for f in done:
        print(f'Winner: {futures[f]} →', f.result())`}
            />

            <ExCard num="M2" title="Timeouts on .result()" difficulty="Medium"
              concepts={["Future.result(timeout=...)","TimeoutError"]}
              goal="Submit a task that sleeps 5 seconds. Call .result(timeout=2) and catch the concurrent.futures.TimeoutError that gets raised, printing 'task is taking too long' instead of blocking for the full 5 seconds."
              hint="The task keeps running in the background even after your .result() call times out — a timeout doesn't cancel the task, it just stops *you* from waiting any longer."
              starter={`from concurrent.futures import ThreadPoolExecutor, TimeoutError
import time

def very_slow():
    time.sleep(5)
    return 'finally done'

with ThreadPoolExecutor(max_workers=1) as executor:
    future = executor.submit(very_slow)
    try:
        result = future.result(timeout=2)   # wait at most 2 seconds
        print('result:', result)
    except TimeoutError:
        print('task is taking too long')
    # the task is still running in the thread pool at this point!`}
            />

            <ExCard num="M3" title="Cancelling Pending Futures" difficulty="Medium"
              concepts={["Future.cancel()","Future.cancelled()","queueing behavior"]}
              goal="Create a pool with max_workers=1. Submit 5 slow tasks (so #2–#5 sit queued while #1 runs). Immediately try to .cancel() futures #2 through #5. Print which ones successfully cancelled (.cancel() returns True) versus which didn't."
              hint="You can only cancel a future that hasn't *started* running yet. Once a worker has picked it up, .cancel() returns False and does nothing."
              starter={`from concurrent.futures import ThreadPoolExecutor
import time

def slow(n):
    time.sleep(2)
    return n

with ThreadPoolExecutor(max_workers=1) as executor:
    futures = [executor.submit(slow, n) for n in range(5)]
    # task 0 starts immediately; tasks 1-4 are queued
    for i, f in enumerate(futures[1:], start=1):
        cancelled = f.cancel()
        print(f'Task {i} cancelled? {cancelled}')
    # check cancelled() status
    for i, f in enumerate(futures):
        print(f'Task {i} - cancelled()={f.cancelled()}')`}
            />

            <ExCard num="M4" title="A Live Progress Counter" difficulty="Medium"
              concepts={["as_completed()","shared state","thread-safety"]}
              goal="Submit 10 tasks with random sleep times. Using as_completed(), print 'N/10 done' after each one finishes, where N is a shared counter you increment. Does this counter even need a Lock?"
              hint="Since as_completed() yields results back in your *calling* thread one at a time, incrementing a plain counter in that loop is already safe — no Lock needed. Contrast this with incrementing a counter *inside* the worker function itself, which would need one."
              starter={`from concurrent.futures import ThreadPoolExecutor, as_completed
import time, random

def task(n):
    time.sleep(random.uniform(0.1, 1.0))
    return n

with ThreadPoolExecutor(max_workers=5) as executor:
    futures = [executor.submit(task, n) for n in range(10)]
    completed = 0
    for f in as_completed(futures):
        completed += 1   # safe: as_completed yields into the MAIN thread
        print(f'{completed}/10 done — task {f.result()} finished')`}
            />

            <ExCard num="M5" title="Retry Logic Around a Flaky Task" difficulty="Medium"
              concepts={["re-submitting on failure","exception handling"]}
              goal="Write flaky_download(url) that randomly raises ConnectionError about 50% of the time, otherwise returns '{url} downloaded'. Submit it for one URL; if .result() raises, re-submit up to 3 times before giving up."
              hint="Wrap your submit-and-wait logic in a small retry loop with a counter, catching the exception each time and breaking out on success."
              starter={`from concurrent.futures import ThreadPoolExecutor
import random

def flaky_download(url):
    if random.random() < 0.5:
        raise ConnectionError(f'network error for {url}')
    return f'{url} downloaded'

url = 'http://example.com/data'
MAX_RETRIES = 3

with ThreadPoolExecutor(max_workers=1) as executor:
    for attempt in range(1, MAX_RETRIES + 1):
        future = executor.submit(flaky_download, url)
        try:
            print(future.result())
            break  # success
        except ConnectionError as e:
            print(f'Attempt {attempt} failed: {e}')
    else:
        print('failed after 3 attempts')`}
            />

            <ExCard num="M6" title="Shared Counter Guarded by a Lock" difficulty="Medium"
              concepts={["threading.Lock","ThreadPoolExecutor","race conditions"]}
              goal="Create a shared count = 0 and a threading.Lock(). Write increment(lock, counter_box) that acquires the lock, reads counter_box[0], sleeps 0.001s, then writes back +1. Submit this 500 times across a pool with max_workers=8. Confirm the final value is exactly 500."
              hint="Every worker thread in the pool shares the same Python process memory — the pool doesn't give each task its own copy of counter_box, so the classic read-sleep-write race condition still applies here exactly like in plain threading."
              starter={`from concurrent.futures import ThreadPoolExecutor
import threading, time

def increment(lock, counter_box):
    with lock:                     # acquire lock before reading
        val = counter_box[0]
        time.sleep(0.001)          # simulate a tiny delay (race window)
        counter_box[0] = val + 1   # write back

lock = threading.Lock()
counter = [0]  # use a list so we can mutate it from worker threads

with ThreadPoolExecutor(max_workers=8) as executor:
    futures = [executor.submit(increment, lock, counter) for _ in range(500)]
    for f in futures:
        f.result()  # wait for all

print(f'final count: {counter[0]}')  # must be exactly 500`}
            />

            <ExCard num="M7" title="Picking max_workers Based on the Machine" difficulty="Medium"
              concepts={["os.cpu_count()","I/O-bound vs CPU-bound sizing"]}
              goal="Print os.cpu_count(). Create a pool with max_workers = min(32, (os.cpu_count() or 1) + 4) — this is the actual default ThreadPoolExecutor uses since Python 3.8. In a comment explain why I/O-bound work benefits from more workers than CPU cores."
              hint="I/O-bound tasks spend most of their time *waiting* (network, disk), during which the GIL is released — so many more threads than cores can be usefully 'in flight' at once. CPU-bound tasks are limited by the GIL to one running at a time regardless of thread count."
              starter={`import os
from concurrent.futures import ThreadPoolExecutor
import time, random

cores = os.cpu_count()
print(f'CPU cores: {cores}')

# Python 3.8+ default for I/O-bound work:
# More workers than cores because threads spend most time waiting, not computing.
io_workers = min(32, (cores or 1) + 4)
print(f'I/O-bound pool size: {io_workers}')

def simulated_api_call(i):
    time.sleep(random.uniform(0.1, 0.5))  # I/O wait
    return f'response-{i}'

with ThreadPoolExecutor(max_workers=io_workers) as executor:
    results = list(executor.map(simulated_api_call, range(20)))
print(f'{len(results)} tasks completed')`}
            />

            <ExCard num="M8" title="The Nested-Executor Trap" difficulty="Medium"
              concepts={["deadlock risk","pool sizing pitfalls"]}
              goal="Create a ThreadPoolExecutor(max_workers=1). Submit a task outer() that itself calls pool.submit(inner) on the SAME pool and then calls .result() on that inner future before returning. Run it and observe it hangs forever. Fix it."
              hint="With max_workers=1, the single worker thread is busy running outer() and blocked waiting on inner()'s result — but there's no second worker free to ever pick up inner(). Fix: use a separate pool for inner(), or increase max_workers to at least 2."
              starter={`from concurrent.futures import ThreadPoolExecutor

def inner():
    return 'inner done'

# This DEADLOCKS with max_workers=1:
pool = ThreadPoolExecutor(max_workers=1)

def outer():
    # outer runs on the pool's only thread, then submits inner to the SAME pool
    f = pool.submit(inner)
    return f.result()   # waits for inner — but no thread is free!

# FIX option 1: increase max_workers
pool2 = ThreadPoolExecutor(max_workers=2)
def outer_fixed():
    f = pool2.submit(inner)
    return f.result()

f = pool2.submit(outer_fixed)
print(f.result())   # works with max_workers=2
pool2.shutdown()`}
            />
          </div>

          {/* ── HARD ── */}
          <div ref={SR("hard")} style={{paddingBottom:44,borderBottom:`1px solid ${T.border}`,marginBottom:36}}>
            {sectionHead("Hard · H1 – H8", T.red)}
            <h2 style={{fontSize:"clamp(18px,2.5vw,26px)",fontWeight:800,color:T.textPri,margin:"0 0 6px"}}>
              Hard Exercises
            </h2>
            <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
              padding:"12px 16px",marginBottom:20,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
              Advanced patterns: GIL benchmarking, dynamic submission, building a pool from scratch, chaining,
              asyncio bridging, and a custom retrying executor.
            </div>

            <ExCard num="H1" title="ThreadPoolExecutor vs ProcessPoolExecutor on CPU-Bound Work" difficulty="Hard"
              concepts={["GIL","ProcessPoolExecutor","CPU-bound vs I/O-bound"]}
              goal="Write a CPU-heavy function count_primes_up_to(n) (trial division up to n=200000). Run it for 4 different n values using ThreadPoolExecutor(max_workers=4) and time it. Then run the same workload using ProcessPoolExecutor(max_workers=4) and time it. Compare."
              hint="For CPU-bound work, ThreadPoolExecutor should barely beat running sequentially (the GIL blocks true parallel Python execution), while ProcessPoolExecutor (separate processes, separate GILs) should show a real speedup close to 4x."
              starter={`from concurrent.futures import ThreadPoolExecutor, ProcessPoolExecutor
import time

def count_primes_up_to(n):
    count = 0
    for num in range(2, n):
        is_prime = all(num % d != 0 for d in range(2, int(num**0.5)+1))
        if is_prime:
            count += 1
    return count

ns = [200_000, 180_000, 190_000, 210_000]

t0 = time.perf_counter()
with ThreadPoolExecutor(max_workers=4) as ex:
    results = list(ex.map(count_primes_up_to, ns))
print(f'Thread pool: {time.perf_counter()-t0:.2f}s  results={results}')

t0 = time.perf_counter()
with ProcessPoolExecutor(max_workers=4) as ex:
    results = list(ex.map(count_primes_up_to, ns))
print(f'Process pool: {time.perf_counter()-t0:.2f}s  results={results}')
# Process pool should be ~4x faster for CPU-bound work`}
            />

            <ExCard num="H2" title="Dynamically Feeding Tasks Into a Running Pool" difficulty="Hard"
              concepts={["producer pattern","Future callbacks","add_done_callback()"]}
              goal="Write a producer loop that generates a new task every 0.3 seconds for 3 seconds (10 tasks total), submitting each one to a pool as it's generated. Use add_done_callback() on each future to print its result the moment it's ready."
              hint="You don't need as_completed() here since you're not waiting on a fixed batch — add_done_callback(fn) fires fn(future) automatically from whichever thread finishes the task, so results can stream out while you're still producing new ones."
              starter={`from concurrent.futures import ThreadPoolExecutor
import time, random

def process_item(item_id):
    time.sleep(random.uniform(0.1, 0.6))
    return f'item-{item_id} processed'

def on_done(future):
    # called by the thread that finished the task
    print('completed:', future.result())

with ThreadPoolExecutor(max_workers=4) as executor:
    for i in range(10):
        future = executor.submit(process_item, i)
        future.add_done_callback(on_done)
        time.sleep(0.3)   # producer rate: one task every 0.3s
# all callbacks fire before the with-block exits`}
            />

            <ExCard num="H3" title="Build a Mini Thread Pool From Scratch" difficulty="Hard"
              concepts={["queue.Queue","worker threads","internal pool mechanics"]}
              goal="Without using concurrent.futures at all, implement your own SimpleThreadPool class: a fixed number of worker threads (started once in __init__) that continuously pull (func, args) tuples off a queue.Queue, execute them, and put results on a second queue.Queue. Add submit() and get_result() methods. Test with 10 tasks on 3 workers."
              hint="Each worker thread runs an infinite while True: task = q.get(); ... loop. This is genuinely close to how ThreadPoolExecutor works internally — building it yourself is the fastest way to stop it feeling like magic."
              starter={`import threading
import queue

class SimpleThreadPool:
    def __init__(self, num_workers):
        self._task_q = queue.Queue()
        self._result_q = queue.Queue()
        self._workers = []
        for _ in range(num_workers):
            t = threading.Thread(target=self._worker, daemon=True)
            t.start()
            self._workers.append(t)

    def _worker(self):
        while True:
            func, args = self._task_q.get()
            try:
                result = func(*args)
                self._result_q.put(result)
            except Exception as e:
                self._result_q.put(e)
            self._task_q.task_done()

    def submit(self, func, *args):
        self._task_q.put((func, args))

    def get_result(self):
        return self._result_q.get()

# Test it
def square(n):
    return n * n

pool = SimpleThreadPool(num_workers=3)
for n in range(10):
    pool.submit(square, n)

results = [pool.get_result() for _ in range(10)]
print(sorted(results))`}
            />

            <ExCard num="H4" title="Chained (Dependent) Futures" difficulty="Hard"
              concepts={["add_done_callback()","chaining stages","non-blocking"]}
              goal="Write stage_1(x) (returns x * 2) and stage_2(y) (returns y + 100). Submit stage_1(5). Using add_done_callback(), when stage_1's future completes, automatically submit stage_2 with its result — all without ever calling a blocking .result() in your main code."
              hint="Your callback receives the finished Future as its argument — call .result() *inside* the callback (safe, since it's already done) to get stage_1's output, then pool.submit(stage_2, that_value) and attach a second callback to print the final answer."
              starter={`from concurrent.futures import ThreadPoolExecutor
import time

pool = ThreadPoolExecutor(max_workers=2)

def stage_1(x):
    time.sleep(0.2)
    return x * 2

def stage_2(y):
    time.sleep(0.2)
    return y + 100

def on_stage_1_done(f1):
    val = f1.result()          # safe: f1 is already done
    print(f'stage_1 → {val}')
    f2 = pool.submit(stage_2, val)
    f2.add_done_callback(on_stage_2_done)

def on_stage_2_done(f2):
    print(f'stage_2 → {f2.result()}')

f1 = pool.submit(stage_1, 5)
f1.add_done_callback(on_stage_1_done)

# main thread is NOT blocked here — it does other work
time.sleep(1)   # just to let both stages finish before the script exits
pool.shutdown(wait=True)`}
            />

            <ExCard num="H5" title="Two-Level Throttling: Pool + Semaphore" difficulty="Hard"
              concepts={["ThreadPoolExecutor","threading.Semaphore","layered concurrency limits"]}
              goal="You have up to 8 worker threads, but the external API allows only 3 concurrent requests. Use a threading.Semaphore(3) acquired inside the task function before the 'network call'. Submit 20 tasks to an 8-worker pool and confirm no more than 3 are ever mid-API-call simultaneously."
              hint="The pool controls how many tasks *start* concurrently; the semaphore inside the task controls how many can pass a specific checkpoint concurrently. They solve different layers of the same problem."
              starter={`from concurrent.futures import ThreadPoolExecutor
import threading, time, datetime

api_semaphore = threading.Semaphore(3)   # max 3 concurrent API calls
active = threading.local()

def api_call(task_id):
    with api_semaphore:
        ts = datetime.datetime.now().strftime('%H:%M:%S.%f')[:-3]
        print(f'[{ts}] task {task_id:02d} — API call IN PROGRESS')
        time.sleep(0.5)   # simulated network latency
    return f'task-{task_id} done'

with ThreadPoolExecutor(max_workers=8) as executor:
    futures = [executor.submit(api_call, i) for i in range(20)]
    for f in futures:
        f.result()`}
            />

            <ExCard num="H6" title="Graceful Shutdown on First Failure" difficulty="Hard"
              concepts={["wait()","FIRST_EXCEPTION","cancellation","error-aware shutdown"]}
              goal="Submit 10 tasks where task #4 raises an exception almost immediately and the rest sleep 3 seconds. Use wait(futures, return_when=FIRST_EXCEPTION) to detect the failure early, then .cancel() everything in not_done, and report how many were successfully cancelled."
              hint="FIRST_EXCEPTION behaves like FIRST_COMPLETED if nothing ever raises — read the docs on what wait() returns when no exception occurs at all. Check future.exception() (not .result()) to identify which future failed without re-raising."
              starter={`from concurrent.futures import ThreadPoolExecutor, wait, FIRST_EXCEPTION
import time

def task(n):
    if n == 4:
        time.sleep(0.05)
        raise RuntimeError(f'task {n} failed!')
    time.sleep(3)
    return n

with ThreadPoolExecutor(max_workers=5) as executor:
    futures = [executor.submit(task, n) for n in range(10)]
    done, not_done = wait(futures, return_when=FIRST_EXCEPTION)

    # find what failed
    for f in done:
        if f.exception():
            print(f'detected failure: {f.exception()}')

    # try to cancel remaining pending futures
    cancelled = sum(1 for f in not_done if f.cancel())
    couldnt   = len(not_done) - cancelled
    print(f'cancelled {cancelled} tasks; {couldnt} already started (could not cancel)')`}
            />

            <ExCard num="H7" title="Bridging ThreadPoolExecutor into asyncio" difficulty="Hard"
              concepts={["loop.run_in_executor","mixing async code with a pool","asyncio"]}
              goal="From an async def main(), use await loop.run_in_executor(pool, blocking_func, arg) to run a blocking function on a ThreadPoolExecutor you created yourself, while a separate async ticker coroutine keeps printing every 0.2s to prove the event loop isn't frozen. Run 5 blocking calls concurrently using asyncio.gather."
              hint="Passing your own pool object as the first argument to run_in_executor (instead of None) lets you control max_workers explicitly rather than relying on asyncio's default executor sizing."
              starter={`import asyncio
from concurrent.futures import ThreadPoolExecutor
import time

pool = ThreadPoolExecutor(max_workers=4)

def blocking_task(n):
    time.sleep(1)   # blocks the thread, NOT the event loop
    return n * n

async def ticker():
    for _ in range(10):
        print('  event loop is alive')
        await asyncio.sleep(0.2)

async def main():
    loop = asyncio.get_running_loop()
    # run ticker and 5 blocking tasks concurrently
    await asyncio.gather(
        ticker(),
        *[loop.run_in_executor(pool, blocking_task, i) for i in range(5)],
    )

asyncio.run(main())
pool.shutdown()`}
            />

            <ExCard num="H8" title="A Self-Retrying Executor Wrapper" difficulty="Hard"
              concepts={["custom executor wrapper","threading.Event","composition"]}
              goal="Design a class RetryingExecutor that wraps a real ThreadPoolExecutor and exposes its own submit(func, *args, retries=3) method: internally, it submits the task, and if .result() raises an exception, retries up to 'retries' times before letting the exception propagate. Return a wrapper object with .result()."
              hint="Easiest if your wrapper's submit() itself runs on a background thread that loops internally: try the real submit + .result(), catch exceptions, retry, and then set the final outcome on your wrapper object. Use a threading.Event to signal 'done' back to the caller."
              starter={`from concurrent.futures import ThreadPoolExecutor
import threading, random

class _FutureWrapper:
    def __init__(self):
        self._event = threading.Event()
        self._result = None
        self._exc = None

    def _set(self, result=None, exc=None):
        self._result = result
        self._exc = exc
        self._event.set()

    def result(self, timeout=None):
        self._event.wait(timeout)
        if self._exc:
            raise self._exc
        return self._result

class RetryingExecutor:
    def __init__(self, max_workers=4):
        self._pool = ThreadPoolExecutor(max_workers=max_workers)

    def submit(self, func, *args, retries=3):
        wrapper = _FutureWrapper()
        def runner():
            for attempt in range(1, retries + 1):
                try:
                    res = self._pool.submit(func, *args).result()
                    wrapper._set(result=res)
                    return
                except Exception as e:
                    print(f'  attempt {attempt} failed: {e}')
            wrapper._set(exc=e)
        threading.Thread(target=runner, daemon=True).start()
        return wrapper

    def shutdown(self):
        self._pool.shutdown(wait=True)

# Test with a flaky function
def flaky():
    if random.random() < 0.7:
        raise RuntimeError('transient error')
    return 'success!'

ex = RetryingExecutor(max_workers=2)
w = ex.submit(flaky, retries=5)
print(w.result())
ex.shutdown()`}
            />
          </div>

          {/* ── Concept Index ── */}
          <div ref={SR("index")} style={{paddingBottom:48}}>
            {sectionHead("Concept Index")}
            <h2 style={{fontSize:"clamp(18px,2.5vw,26px)",fontWeight:800,color:T.textPri,margin:"0 0 10px"}}>
              Concept Index
            </h2>
            <p style={{fontSize:13.5,color:T.textSec,lineHeight:1.7,margin:"0 0 12px"}}>
              Use this table to jump to the exercises that drill a specific concept.
            </p>
            <ConceptIndex/>
          </div>

        </div>
      </div>
    </div>
  );
}
